import { useEffect, useMemo, useRef, useState } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, rawFetch } from "@/lib/api"
import { vault } from "@/lib/vault"
import { useT } from "@/lib/i18n"
import { Badge, Button, Card, CardBody, Input, Sheet, Spinner, Toast, Textarea } from "@/components/ui"
import { cn, formatBytes, timeAgo } from "@/lib/utils"
import type { MediaAlbumOut, MediaItemOut, MediaListOut, MediaStatsOut } from "@/lib/types"
import {
  DownloadIcon, EyeOffIcon, EyeIcon, FolderIcon, HeartIcon, ImagesIcon,
  LockIcon, LockOpenIcon, PlusIcon, SearchIcon, Trash2Icon, UploadIcon, XIcon, MessageCircleIcon,
  CheckIcon, CheckSquareIcon, FileArchiveIcon, ChevronLeftIcon, ChevronRightIcon, PlayIcon } from "lucide-react"

export const Route = createFileRoute("/_app/media")({
  component: MediaPage,
})

type Filter = "all" | "favorites" | "videos" | "hidden" | { album: string }

function filterKey(f: Filter): string { return typeof f === "string" ? f : `album:${f.album}` }

function MediaPage() {
  const t = useT()
  const qc = useQueryClient()
  const [filter, setFilter] = useState<Filter>("all")
  const [q, setQ] = useState("")
  const [open, setOpen] = useState<MediaItemOut | null>(null)
  const [unlocking, setUnlocking] = useState(false)
  const [vaultOpen, setVaultOpen] = useState(vault.isOpen())
  const [toast, setToast] = useState<{ kind: "ok" | "destructive"; text: string } | null>(null)
  const [newAlbum, setNewAlbum] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  // Explicit select mode: checkboxes stay visible and a tap toggles instead
  // of opening — works the same in Hidden, Favourites and albums.
  const [selectMode, setSelectMode] = useState(false)
  const fileInput = useRef<HTMLInputElement | null>(null)

  // Selection is per-view: switching filter / search / locking the vault clears it.
  useEffect(() => { setSelected(new Set()) }, [filter, q, vaultOpen])
  // Esc clears the selection (when no lightbox is open — it closes that first).
  useEffect(() => {
    if (selected.size === 0) return
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !open) setSelected(new Set()) }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [selected.size, open])

  // Re-check the vault every 30s so the UI locks itself when the token dies.
  useEffect(() => {
    const timer = setInterval(() => setVaultOpen(vault.isOpen()), 30_000)
    return () => clearInterval(timer)
  }, [])
  // Toasts auto-dismiss.
  useEffect(() => {
    if (!toast) return
    const timer = setTimeout(() => setToast(null), 3500)
    return () => clearTimeout(timer)
  }, [toast])

  const isHidden = filter === "hidden"
  const list = useQuery({
    queryKey: ["media", filterKey(filter), q, vaultOpen],
    queryFn: () => api<MediaListOut>("/api/media", {
      query: {
        limit: 120,
        favorite: filter === "favorites" ? true : undefined,
        kind: filter === "videos" ? "video" : undefined,
        hidden: isHidden ? true : undefined,
        album_id: typeof filter === "object" ? filter.album : undefined,
        q: q.trim() || undefined,
      },
      headers: vault.headers(),
    }),
    enabled: !isHidden || vaultOpen,
  })
  const stats = useQuery({ queryKey: ["media-stats"], queryFn: () => api<MediaStatsOut>("/api/media/stats") })
  const albums = useQuery({ queryKey: ["media-albums"], queryFn: () => api<MediaAlbumOut[]>("/api/media/albums") })

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["media"] })
    qc.invalidateQueries({ queryKey: ["media-stats"] })
    qc.invalidateQueries({ queryKey: ["media-albums"] })
  }

  const patch = useMutation({
    mutationFn: (args: { id: string; body: Record<string, unknown> }) =>
      api<MediaItemOut>(`/api/media/${args.id}`, { method: "PATCH", body: args.body, headers: vault.headers() }),
    onSuccess: (item) => { invalidate(); setOpen((cur) => (cur && cur.id === item.id ? item : cur)) },
    onError: (e) => setToast({ kind: "destructive", text: (e as Error).message }),
  })
  // Bulk actions on the selection: PATCH / DELETE per item, in parallel.
  const bulk = useMutation({
    mutationFn: async (args: { ids: string[]; body?: Record<string, unknown>; del?: boolean }) => {
      const results = await Promise.allSettled(args.ids.map((id) =>
        args.del
          ? api(`/api/media/${id}`, { method: "DELETE", headers: vault.headers() })
          : api(`/api/media/${id}`, { method: "PATCH", body: args.body, headers: vault.headers() }),
      ))
      const failed = results.filter((r) => r.status === "rejected").length
      return { ok: results.length - failed, failed }
    },
    onSuccess: ({ ok, failed }, args) => {
      invalidate()
      setSelected(new Set())
      const what = args.del ? "deleted" : args.body?.hidden === true ? "hidden" : args.body?.hidden === false ? "unhidden"
        : args.body?.favorite === true ? "favourited" : "updated"
      setToast({ kind: failed ? "destructive" : "ok", text: `${t(`media.bulk.${what}`, { n: ok })}${failed ? t("media.bulk.failed", { n: failed }) : ""}` })
    },
    onError: (e) => setToast({ kind: "destructive", text: (e as Error).message }),
  })
  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/media/${id}`, { method: "DELETE", headers: vault.headers() }),
    onSuccess: () => { invalidate(); setOpen(null); setToast({ kind: "ok", text: t("media.toast.deleted") }) },
    onError: (e) => setToast({ kind: "destructive", text: (e as Error).message }),
  })
  const upload = useMutation({
    mutationFn: async (files: FileList) => {
      for (const f of Array.from(files)) {
        const fd = new FormData()
        fd.append("file", f)
        fd.append("hidden", String(isHidden))
        if (typeof filter === "object") fd.append("album_id", filter.album)
        await api("/api/media/upload", { method: "POST", rawBody: fd, headers: vault.headers() })
      }
    },
    onSuccess: () => { invalidate(); setToast({ kind: "ok", text: t("media.toast.added") }) },
    onError: (e) => setToast({ kind: "destructive", text: (e as Error).message }),
  })
  const createAlbum = useMutation({
    mutationFn: (name: string) => api<MediaAlbumOut>("/api/media/albums", { method: "POST", body: { name } }),
    onSuccess: (a) => { invalidate(); setNewAlbum(false); setFilter({ album: a.id }) },
  })
  const deleteAlbum = useMutation({
    mutationFn: (id: string) => api(`/api/media/albums/${id}`, { method: "DELETE" }),
    onSuccess: () => { invalidate(); setFilter("all") },
  })
  const downloadZip = useMutation({
    mutationFn: async (ids: string[]) => {
      const r = await rawFetch("/api/media/zip", {
        method: "POST",
        headers: vault.headers(),
        body: { ids },   // rawFetch JSON-encodes objects itself
      })
      if (!r.ok) {
        const detail = await r.json().then((b) => b?.detail).catch(() => null)
        throw new Error(detail || `HTTP ${r.status}`)
      }
      const blob = await r.blob()
      const cd = r.headers.get("Content-Disposition") || ""
      const name = /filename="([^"]+)"/.exec(cd)?.[1] || "lucy-media.zip"
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a"); a.href = url; a.download = name; a.click()
      setTimeout(() => URL.revokeObjectURL(url), 10_000)
      return Number(r.headers.get("X-Lucy-Zip-Count") || ids.length)
    },
    onSuccess: (count) => { setToast({ kind: "ok", text: count === 1 ? t("media.toast.zipOne") : t("media.toast.zipMany", { n: count }) }); setSelected(new Set()) },
    onError: (e) => setToast({ kind: "destructive", text: (e as Error).message }),
  })

  function toggleSelected(id: string) {
    setSelected((cur) => { const next = new Set(cur); if (next.has(id)) next.delete(id); else next.add(id); return next })
  }

  const items = list.data?.items ?? []
  const albumName = useMemo(
    () => (typeof filter === "object" ? albums.data?.find((a) => a.id === filter.album)?.name : null),
    [filter, albums.data],
  )

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">{t("media.title")}</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("media.sub")} {stats.data ? t("media.inLibrary", { n: stats.data.total }) : ""}
            {stats.data && stats.data.hidden > 0 ? ` · ${t("media.hiddenCount", { n: stats.data.hidden })}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileInput} type="file" accept="image/*" multiple className="hidden"
            onChange={(e) => { if (e.target.files?.length) upload.mutate(e.target.files); e.target.value = "" }} />
          <Button variant="outline" size="sm" onClick={() => fileInput.current?.click()} disabled={upload.isPending}>
            {upload.isPending ? <Spinner /> : <UploadIcon className="h-3.5 w-3.5" />} {t("media.add")}
          </Button>
          {vaultOpen ? (
            <Button variant="outline" size="sm" onClick={() => { vault.lock(); setVaultOpen(false); if (isHidden) setFilter("all") }}>
              <LockOpenIcon className="h-3.5 w-3.5" /> {t("media.lockVault")}
            </Button>
          ) : null}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Chip active={filter === "all"} onClick={() => setFilter("all")}><ImagesIcon className="h-3.5 w-3.5" /> {t("media.all")}</Chip>
        <Chip active={filter === "favorites"} onClick={() => setFilter("favorites")}><HeartIcon className="h-3.5 w-3.5" /> {t("media.favorites")}{stats.data ? ` ${stats.data.favorites}` : ""}</Chip>
        <Chip active={filter === "videos"} onClick={() => setFilter("videos")}><PlayIcon className="h-3.5 w-3.5" /> {t("media.videos")}</Chip>
        <Chip active={isHidden} onClick={() => (vaultOpen ? setFilter("hidden") : setUnlocking(true))}>
          {vaultOpen ? <LockOpenIcon className="h-3.5 w-3.5" /> : <LockIcon className="h-3.5 w-3.5" />} {t("media.hidden")}{stats.data ? ` ${stats.data.hidden}` : ""}
        </Chip>
        <Chip active={selectMode} onClick={() => { if (selectMode) setSelected(new Set()); setSelectMode((v) => !v) }}>
          <CheckSquareIcon className="h-3.5 w-3.5" /> {selectMode ? t("media.done") : t("media.select")}
        </Chip>
        <span className="mx-1 h-4 w-px bg-border" />
        {(albums.data ?? []).map((a) => (
          <Chip key={a.id} active={typeof filter === "object" && filter.album === a.id} onClick={() => setFilter({ album: a.id })}>
            <FolderIcon className="h-3.5 w-3.5" /> {a.name} <span className="opacity-60">{a.count}</span>
          </Chip>
        ))}
        <Chip active={false} onClick={() => setNewAlbum(true)}><PlusIcon className="h-3.5 w-3.5" /> {t("media.album")}</Chip>
        <div className="ml-auto relative">
          <SearchIcon className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("media.searchPrompts")} className="pl-8 h-8 w-56 text-xs" />
        </div>
      </div>

      {typeof filter === "object" && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <FolderIcon className="h-3.5 w-3.5" /> {t("media.album")} <b className="text-foreground">{albumName}</b>
          <button className="underline hover:text-destructive" onClick={() => { if (confirm(t("media.deleteAlbumConfirm", { name: albumName ?? "" }))) deleteAlbum.mutate(filter.album) }}>{t("media.deleteAlbum")}</button>
          <ShareAlbum albumId={filter.album} />
        </div>
      )}

      {/* Grid */}
      {list.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner /> {t("media.loading")}</div>
      ) : items.length === 0 ? (
        <Card><CardBody>
          <div className="py-10 text-center text-sm text-muted-foreground">
            {isHidden ? t("media.emptyHidden") :
              q ? t("media.emptySearch") :
              <>{t("media.emptyBefore")} <Link to="/chat" className="underline">{t("media.emptyLink")}</Link> {t("media.emptyAfter")}</>}
          </div>
        </CardBody></Card>
      ) : (
        <div className="columns-2 gap-3 sm:columns-3 lg:columns-4 xl:columns-5 [&>*]:mb-3 [&>*]:break-inside-avoid">
          {items.map((m) => (
            <Tile
              key={m.id}
              item={m}
              selected={selected.has(m.id)}
              selecting={selectMode || selected.size > 0}
              onOpen={() => setOpen(m)}
              onToggle={() => toggleSelected(m.id)}
              onFav={() => patch.mutate({ id: m.id, body: { favorite: !m.favorite } })}
            />
          ))}
        </div>
      )}
      {list.data?.next_before && (
        <p className="text-xs text-muted-foreground">{t("media.showingLatest", { n: items.length, total: list.data.total })}</p>
      )}

      {/* Selection bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 z-40 -translate-x-1/2 animate-fade-in">
          <div className="glass flex items-center gap-1.5 rounded-full border border-border py-1.5 pl-4 pr-1.5 shadow-glass">
            <span className="mr-1 text-xs font-medium tabular-nums">{t("media.selected", { n: selected.size })}</span>
            <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setSelected(new Set(items.map((m) => m.id)))} disabled={selected.size === items.length}>
              <CheckSquareIcon className="h-3.5 w-3.5" /> {t("media.selectAll", { n: items.length })}
            </Button>
            <Button size="sm" className="rounded-full" onClick={() => downloadZip.mutate(Array.from(selected))} disabled={downloadZip.isPending}>
              {downloadZip.isPending ? <Spinner /> : <FileArchiveIcon className="h-3.5 w-3.5" />} {t("media.downloadZip")}
            </Button>
            {isHidden ? (
              <Button variant="outline" size="sm" className="rounded-full" disabled={bulk.isPending || !vaultOpen}
                onClick={() => bulk.mutate({ ids: Array.from(selected), body: { hidden: false } })}>
                {bulk.isPending ? <Spinner /> : <EyeIcon className="h-3.5 w-3.5" />} {t("media.unhide")}
              </Button>
            ) : (
              <Button variant="outline" size="sm" className="rounded-full" disabled={bulk.isPending}
                onClick={() => bulk.mutate({ ids: Array.from(selected), body: { hidden: true } })}>
                {bulk.isPending ? <Spinner /> : <EyeOffIcon className="h-3.5 w-3.5" />} {t("media.hide")}
              </Button>
            )}
            <Button variant="outline" size="sm" className="rounded-full" disabled={bulk.isPending}
              onClick={() => bulk.mutate({ ids: Array.from(selected), body: { favorite: true } })}>
              <HeartIcon className="h-3.5 w-3.5" /> {t("media.favourite")}
            </Button>
            <Button variant="outline" size="sm" className="rounded-full text-destructive" disabled={bulk.isPending}
              onClick={() => { if (window.confirm(selected.size === 1 ? t("media.deleteOneConfirm") : t("media.deleteManyConfirm", { n: selected.size }))) bulk.mutate({ ids: Array.from(selected), del: true }) }}>
              <Trash2Icon className="h-3.5 w-3.5" /> {t("media.delete")}
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" aria-label={t("media.clearSelection")} onClick={() => setSelected(new Set())}>
              <XIcon className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {open && (
        <Lightbox
          item={open}
          albums={albums.data ?? []}
          onOpenItem={(it) => { setOpen(it); invalidate() }}
          vaultOpen={vaultOpen}
          index={items.findIndex((m) => m.id === open.id)}
          total={items.length}
          onStep={(d) => { const i = items.findIndex((m) => m.id === open.id); const n = items[i + d]; if (n) setOpen(n) }}
          onClose={() => setOpen(null)}
          onFav={() => patch.mutate({ id: open.id, body: { favorite: !open.favorite } })}
          onHide={() => {
            if (open.hidden && !vaultOpen) { setUnlocking(true); return }
            patch.mutate({ id: open.id, body: { hidden: !open.hidden } })
            setToast({ kind: "ok", text: open.hidden ? t("media.toast.backToLibrary") : t("media.toast.movedHidden") })
            setOpen(null)
          }}
          onAlbum={(albumId) => patch.mutate({ id: open.id, body: albumId ? { album_id: albumId } : { clear_album: true } })}
          onDelete={() => { if (confirm(t("media.deleteForGood"))) remove.mutate(open.id) }}
        />
      )}

      {/* Vault unlock */}
      <UnlockSheet open={unlocking} onClose={() => setUnlocking(false)} onUnlocked={() => { setUnlocking(false); setVaultOpen(true); setFilter("hidden") }} />

      {/* New album */}
      <Sheet open={newAlbum} onClose={() => setNewAlbum(false)} title={t("media.newAlbum")}>
        <form onSubmit={(e) => { e.preventDefault(); const name = (new FormData(e.currentTarget).get("name") as string).trim(); if (name) createAlbum.mutate(name) }} className="space-y-3">
          <Input name="name" placeholder={t("media.albumPlaceholder")} autoFocus />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setNewAlbum(false)}>{t("media.cancel")}</Button>
            <Button type="submit" size="sm" disabled={createAlbum.isPending}>{t("media.create")}</Button>
          </div>
        </form>
      </Sheet>

      {toast && <Toast variant={toast.kind}>{toast.text}</Toast>}
    </div>
  )
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} className={cn(
      "inline-flex items-center gap-1.5 rounded-full border px-3 h-8 text-xs transition-colors",
      active ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-accent",
    )}>{children}</button>
  )
}

/** Authenticated thumbnail — the API needs a Bearer (and, for hidden items, the vault token). */
function AuthedThumb({ item, className, full = false }: { item: MediaItemOut; className?: string; full?: boolean }) {
  const [src, setSrc] = useState<string | null>(null)
  const [locked, setLocked] = useState(false)
  useEffect(() => {
    let active = true; let url: string | null = null
    setSrc(null); setLocked(false)
    rawFetch(full ? item.url : item.thumb_url, { headers: vault.headers() }).then(async (r) => {
      if (!active) return
      if (r.status === 403) { setLocked(true); return }
      if (!r.ok) return
      url = URL.createObjectURL(await r.blob()); setSrc(url)
    }).catch(() => undefined)
    return () => { active = false; if (url) URL.revokeObjectURL(url) }
  }, [item.id, item.hidden, full, item.url, item.thumb_url])
  if (locked) return <div className={cn("grid place-items-center bg-muted text-muted-foreground", className)}><LockIcon className="h-5 w-5" /></div>
  if (!src) return <div className={cn("bg-muted/40 animate-pulse", className)} />
  if (full && item.kind === "video") {
    return <video src={src} controls autoPlay playsInline loop className={cn(className, "bg-black")} />
  }
  return <img src={src} alt={item.title || item.prompt || "media"} className={className} loading="lazy" />
}

function Tile({ item, selected, selecting, onOpen, onToggle, onFav }: {
  item: MediaItemOut; selected: boolean; selecting: boolean
  onOpen: () => void; onToggle: () => void; onFav: () => void
}) {
  const t = useT()
  const ratio = item.width && item.height ? item.width / item.height : 1
  return (
    <div
      className={cn(
        "group relative overflow-hidden rounded-lg border bg-card transition-[box-shadow,transform] duration-150",
        selecting ? "cursor-pointer" : "cursor-zoom-in",
        selected && "ring-2 ring-primary ring-offset-2 ring-offset-background scale-[0.98]",
      )}
      style={{ aspectRatio: Math.min(Math.max(ratio, 0.45), 2.4) }}
      onClick={(e) => { if (selecting || e.metaKey || e.ctrlKey) onToggle(); else onOpen() }}
    >
      <AuthedThumb item={item} className={cn("h-full w-full object-cover transition-opacity", selected && "opacity-80")} />
      {item.kind === "video" && (
        <span className="pointer-events-none absolute inset-0 grid place-items-center">
          <span className="grid h-11 w-11 place-items-center rounded-full bg-black/55 text-white ring-1 ring-white/30 backdrop-blur-sm transition group-hover:scale-105">
            <PlayIcon className="ml-0.5 h-5 w-5" fill="currentColor" />
          </span>
        </span>
      )}
      <button
        aria-label={selected ? t("media.deselect") : t("media.select")}
        aria-pressed={selected}
        onClick={(e) => { e.stopPropagation(); onToggle() }}
        className={cn(
          "absolute top-2 left-2 grid h-5 w-5 place-items-center rounded-full border transition-opacity",
          selected
            ? "border-primary bg-primary text-primary-foreground opacity-100"
            : "border-border bg-black/40 backdrop-blur opacity-0 group-hover:opacity-100 hover:bg-black/60",
          selecting && "opacity-100",
        )}
      >
        <CheckIcon className="h-3 w-3" strokeWidth={3} />
      </button>
      <button
        aria-label={item.favorite ? t("media.unfavorite") : t("media.favorite")}
        onClick={(e) => { e.stopPropagation(); onFav() }}
        className={cn("absolute top-2 right-2 rounded-full p-1.5 backdrop-blur bg-black/40 text-white transition-opacity",
          item.favorite ? "opacity-100" : "opacity-0 group-hover:opacity-100")}
      >
        <HeartIcon className={cn("h-3.5 w-3.5", item.favorite && "fill-current")} />
      </button>
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2 text-[10px] text-white opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="line-clamp-2">{item.title || item.prompt || item.source}</div>
      </div>
    </div>
  )
}

function Lightbox({ item, albums, vaultOpen, index, total, onStep, onClose, onFav, onHide, onAlbum, onDelete, onOpenItem }: {
  item: MediaItemOut; albums: MediaAlbumOut[]; vaultOpen: boolean
  index: number; total: number; onStep: (delta: 1 | -1) => void
  onClose: () => void; onFav: () => void; onHide: () => void; onAlbum: (id: string | null) => void; onDelete: () => void
  onOpenItem: (it: MediaItemOut) => void
}) {
  const t = useT()
  const hasPrev = index > 0, hasNext = index >= 0 && index < total - 1
  // Instruction edits ("change the background") → a new version next to this one.
  const [instruction, setInstruction] = useState("")
  const [editing, setEditing] = useState(false)
  const [editErr, setEditErr] = useState<string | null>(null)
  const versions = useQuery({
    queryKey: ["media-versions", item.parent_id ?? item.id],
    queryFn: () => api<MediaItemOut[]>(`/api/media/${item.id}/versions`),
    enabled: item.kind === "image",
  })
  async function runEdit() {
    const ins = instruction.trim()
    if (!ins) return
    setEditing(true); setEditErr(null)
    try {
      const out = await api<{ id: string }>(`/api/media/${item.id}/edit`, { method: "POST", body: { instruction: ins } })
      const it = await api<MediaItemOut>(`/api/media/${out.id}`)
      setInstruction("")
      onOpenItem(it)
    } catch (e) {
      setEditErr((e as { detail?: string }).detail || (e as Error).message)
    } finally { setEditing(false) }
  }
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
      else if (e.key === "ArrowLeft" && hasPrev) onStep(-1)
      else if (e.key === "ArrowRight" && hasNext) onStep(1)
    }
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey)
  }, [onClose, onStep, hasPrev, hasNext])
  // Swipe on touch screens.
  const touchX = useRef<number | null>(null)
  async function download() {
    const r = await rawFetch(`${item.url}?download=1`, { headers: vault.headers() })
    if (!r.ok) return
    const url = URL.createObjectURL(await r.blob())
    const a = document.createElement("a"); a.href = url; a.download = `lucy-${item.id.slice(0, 8)}.${item.ext}`; a.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/85 animate-fade-in flex" onClick={onClose}>
      <button className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={onClose} aria-label={t("media.close")}><XIcon className="h-6 w-6" /></button>
      <div
        className="relative flex-1 grid place-items-center p-6 min-w-0"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => { touchX.current = e.touches[0]?.clientX ?? null }}
        onTouchEnd={(e) => {
          const x0 = touchX.current; touchX.current = null
          const x1 = e.changedTouches[0]?.clientX
          if (x0 == null || x1 == null) return
          if (x1 - x0 > 60 && hasPrev) onStep(-1)
          else if (x0 - x1 > 60 && hasNext) onStep(1)
        }}
      >
        <AuthedThumb item={item} full className="max-h-[88vh] max-w-full object-contain rounded-md shadow-2xl" />
        {hasPrev && (
          <button
            type="button"
            onClick={() => onStep(-1)}
            aria-label={t("media.previous")}
            className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-card/80 text-foreground ring-1 ring-inset ring-border backdrop-blur hover:bg-secondary"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
        )}
        {hasNext && (
          <button
            type="button"
            onClick={() => onStep(1)}
            aria-label={t("media.next")}
            className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-card/80 text-foreground ring-1 ring-inset ring-border backdrop-blur hover:bg-secondary"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        )}
        {total > 1 && index >= 0 && (
          <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-card/80 px-2.5 py-1 font-mono text-[11px] text-muted-foreground ring-1 ring-inset ring-border">
            {index + 1} / {total}
          </span>
        )}
      </div>
      <aside className="w-80 shrink-0 bg-card border-l p-4 overflow-y-auto text-sm space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-wrap gap-1.5">
          {item.kind === "video" && <Badge><PlayIcon className="mr-1 h-3 w-3" />{t("media.video")}</Badge>}
          <Badge>{item.source}</Badge>
          {item.model && <Badge>{item.model}</Badge>}
          {item.hidden && <Badge><LockIcon className="h-3 w-3 mr-1" />{t("media.badgeHidden")}</Badge>}
        </div>
        {item.prompt && <p className="text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap">{item.prompt}</p>}
        <dl className="grid grid-cols-2 gap-y-1 text-xs text-muted-foreground">
          <dt>{t("media.created")}</dt><dd className="text-foreground">{timeAgo(item.created_at)}</dd>
          {item.width && <><dt>{t("media.size")}</dt><dd className="text-foreground">{item.width}×{item.height}{item.size_bytes ? ` · ${formatBytes(item.size_bytes)}` : ""}</dd></>}
          {item.provider && <><dt>{t("media.provider")}</dt><dd className="text-foreground">{item.provider}</dd></>}
        </dl>
        {item.kind === "image" && (
          <div className="space-y-2 rounded-xl bg-secondary/60 p-3 ring-1 ring-inset ring-border">
            <div className="text-[12px] font-medium">{t("media.editTitle")}</div>
            <Textarea rows={2} value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder={t("media.editHint")} />
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-muted-foreground">{t("media.editNote")}</span>
              <Button size="sm" onClick={runEdit} disabled={editing || !instruction.trim()}>{editing ? <Spinner /> : null}{t("media.editGo")}</Button>
            </div>
            {editErr && <Toast variant="destructive">{editErr}</Toast>}
            {(versions.data?.length ?? 0) > 1 && (
              <div className="space-y-1">
                <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">{t("media.versions")}</div>
                <div className="flex flex-wrap gap-1.5">
                  {(versions.data ?? []).map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => onOpenItem(v)}
                      title={v.edit_instruction || t("media.original")}
                      className={`h-14 w-14 overflow-hidden rounded-lg ring-2 ${v.id === item.id ? "ring-primary" : "ring-border"}`}
                    >
                      <AuthedThumb item={v} className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
                {item.edit_instruction && <div className="text-[11px] italic text-muted-foreground">v{item.version}: {item.edit_instruction}</div>}
              </div>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" size="sm" onClick={onFav}><HeartIcon className={cn("h-3.5 w-3.5", item.favorite && "fill-current")} /> {item.favorite ? t("media.favorited") : t("media.favorite")}</Button>
          <Button variant="outline" size="sm" onClick={download}><DownloadIcon className="h-3.5 w-3.5" /> {t("media.download")}</Button>
          <Button variant="outline" size="sm" onClick={onHide}>
            {item.hidden ? <EyeIcon className="h-3.5 w-3.5" /> : <EyeOffIcon className="h-3.5 w-3.5" />} {item.hidden ? (vaultOpen ? t("media.unhide") : t("media.unlockToUnhide")) : t("media.hide")}
          </Button>
          <Button variant="outline" size="sm" onClick={onDelete} className="text-destructive"><Trash2Icon className="h-3.5 w-3.5" /> {t("media.delete")}</Button>
        </div>
        <label className="block text-xs text-muted-foreground">
          {t("media.album")}
          <select className="mt-1 w-full h-8 rounded-md border bg-background px-2 text-xs" value={item.album_id ?? ""} onChange={(e) => onAlbum(e.target.value || null)}>
            <option value="">{t("media.albumNone")}</option>
            {albums.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </label>
        {item.conversation_id && (
          <Link to="/conversations" className="inline-flex items-center gap-1.5 text-xs underline text-muted-foreground hover:text-foreground">
            <MessageCircleIcon className="h-3.5 w-3.5" /> {t("media.openConversation")}
          </Link>
        )}
      </aside>
    </div>
  )
}

/** Signed, expiring link for an album; hidden pictures never leave the vault. */
function ShareAlbum({ albumId }: { albumId: string }) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const [hours, setHours] = useState(24 * 7)
  const [link, setLink] = useState<{ url: string; expires_at: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  async function make() {
    setBusy(true); setErr(null); setLink(null)
    try {
      const out = await api<{ url: string; expires_at: string }>(`/api/media/albums/${albumId}/share`, { method: "POST", body: { expires_hours: hours } })
      setLink({ url: out.url.startsWith("http") ? out.url : `${window.location.origin}${out.url}`, expires_at: out.expires_at })
    } catch (e) { setErr((e as { detail?: string }).detail || (e as Error).message) } finally { setBusy(false) }
  }
  async function copy() {
    if (!link) return
    try { await navigator.clipboard.writeText(link.url); setCopied(true); setTimeout(() => setCopied(false), 1500) } catch { /* fallback: the field is selectable */ }
  }
  return (
    <>
      <button className="underline hover:text-foreground" onClick={() => { setOpen(true); setLink(null) }}>{t("media.share")}</button>
      <Sheet open={open} onClose={() => setOpen(false)} title={t("media.shareTitle")}>
        <div className="space-y-3 text-sm">
          <p className="text-xs text-muted-foreground">{t("media.shareNote")}</p>
          <div className="flex flex-wrap items-center gap-2">
            <select value={hours} onChange={(e) => setHours(parseInt(e.target.value, 10))} className="rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground [&>option]:bg-card">
              <option value={24}>{t("media.share1d")}</option>
              <option value={24 * 7}>{t("media.share7d")}</option>
              <option value={24 * 30}>{t("media.share30d")}</option>
            </select>
            <Button size="sm" onClick={make} disabled={busy}>{busy ? <Spinner /> : null}{t("media.shareMake")}</Button>
          </div>
          {link && (
            <div className="space-y-1.5">
              <Input readOnly value={link.url} onFocus={(e) => e.currentTarget.select()} className="font-mono text-xs" />
              <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                <span>{t("media.shareExpires")} {new Date(link.expires_at).toLocaleString()}</span>
                <Button size="sm" variant="outline" onClick={copy}>{copied ? t("media.shareCopied") : t("media.shareCopy")}</Button>
              </div>
            </div>
          )}
          {err && <Toast variant="destructive">{err}</Toast>}
        </div>
      </Sheet>
    </>
  )
}

function UnlockSheet({ open, onClose, onUnlocked }: { open: boolean; onClose: () => void; onUnlocked: () => void }) {
  const t = useT()
  const [pw, setPw] = useState("")
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  useEffect(() => { if (open) { setPw(""); setErr(null) } }, [open])
  async function submit(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setErr(null)
    try { await vault.unlock(pw); onUnlocked() }
    catch (ex) { setErr((ex as Error).message === "wrong password" ? t("media.wrongPassword") : (ex as Error).message) }
    finally { setBusy(false) }
  }
  return (
    <Sheet open={open} onClose={onClose} title={t("media.unlockTitle")}>
      <form onSubmit={submit} className="space-y-3">
        <p className="text-xs text-muted-foreground">{t("media.unlockHint")}</p>
        <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder={t("media.password")} autoFocus autoComplete="current-password" />
        {err && <p className="text-xs text-destructive">{err}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>{t("media.cancel")}</Button>
          <Button type="submit" size="sm" disabled={busy || !pw}>{busy ? <Spinner /> : <LockOpenIcon className="h-3.5 w-3.5" />} {t("media.unlock")}</Button>
        </div>
      </form>
    </Sheet>
  )
}
